import { useState } from 'react';
import { IonButton } from '@ionic/react';
import { CareMapDrawerState } from '../../caremap/CareMapDrawerState';
import { useSubscription } from '@meraki-internal/state';
import { CareMapDrawerPage } from '../../components/CareMapDrawerPage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { CareMapState } from '../../caremap/CareMapState';
import { Menu } from '../../components/Menu';
import { DeleteIcon } from '../../theme/Icons';
import { AppPrincipal, isAnonymousViewer } from '../../auth/AppPrincipal';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { StructuredRecordEditor } from './StructuredRecordEditor';
import { IStructuredRecordDrawerProps } from './StructuredRecordDrawerProps';

/**
 * Care-map-route drawer host for a structured record editor -- the counterpart to
 * StructuredRecordEditorDrawer for entry points that live under the care-map drawer route
 * rather than a query-param drawer.
 */
export const StructuredRecordEditorCareMapDrawer = <T,>({ descriptor, formState, id }: IStructuredRecordDrawerProps<T>) => {
    const history = useInstance(HistoryViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const drawerState = useInstance(CareMapDrawerState);
    useSubscription(() => drawerState);

    const [saving, setSaving] = useState(false);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    useSubscription(() => formState);

    const isNew = id === descriptor.newId;
    const record = formState.state.record;

    const isViewer = !careMapState.hasPermissionTo('put');
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const canAddEdit = !isViewer && !isAnonymous;

    const onDidEnter = () => {
        drawerState.setIsMaskingFocusedCareMap(true);

        if (!isNew && record) {
            mixpanel.track(`${descriptor.trackingNoun} Viewed`, () => descriptor.buildTrackingProps(record));
        }
    };

    const onWillLeave = () => {
        drawerState.setIsMaskingFocusedCareMap(false);
    };

    const [isValid, setIsValid] = useState<boolean>(false);

    if (!careMapState.getActiveCaremap()) {
        return null;
    }

    const deleteRecord = async () => {
        setSaving(true);
        formState.delete();
        history.back();
    };

    const save = async () => {
        if (record) {
            setSaving(true);
            formState.updateCurrentPatient();
            history.back();
        }
    };

    const cancel = () => {
        formState.clear();
        history.back();
    };

    return (
        <CareMapDrawerPage
            hasBack
            enteredHandler={onDidEnter}
            leavingHandler={onWillLeave}
            data-type="structured-record-editor"
            title={`${isNew ? 'Add' : 'Edit'} ${descriptor.singular}`}
            buttons={
                canAddEdit && <>
                    <IonButton data-type="save-button" disabled={!isValid || saving} onClick={save}>Save</IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={cancel}>Cancel</IonButton>
                </>
            }
        >
            {(canAddEdit && !isNew) ? (
                <div style={{marginTop: -8, display: 'flex', justifyContent: 'end'}}>
                    <Menu data-id={`edit-${descriptor.routeSegment}-menu`} actions={[{
                        Icon: DeleteIcon,
                        role: 'destructive',
                        text: `Delete ${descriptor.singular}`,
                        handler: deleteRecord,
                        'data-type': 'delete-button',
                        disabled: saving
                    }]} />
                </div>
            ) : <div data-type="no-menu" />}

            <StructuredRecordEditor
                descriptor={descriptor}
                formState={formState}
                id={id}
                readOnly={!canAddEdit}
                reportValidity={(valid) => setIsValid(valid)}
            />
        </CareMapDrawerPage>
    );
};
